import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@1trading/react';
import { CircleAlert } from 'lucide-react';
import { describeAuthError, normalizeAuthErrorReason, REAUTH_QUERY, withoutAuthErrorParams } from '@/lib/auth/auth-error';

interface OidcErrorProps {
  /** Raw `?authError=` reason slug the auth callback redirected back with, if any */
  message?: string | null;
}

/** Rebuilds the current location minus the error flag, so a retry returns the user where they were headed instead of the app root. Forces a fresh Okta sign-in: a silent SSO retry just hits the same failure. */
const buildRetryHref = (): string => {
  const query = withoutAuthErrorParams(new URLSearchParams(window.location.search)).toString();
  const returnTo = `${window.location.pathname}${query ? `?${query}` : ''}`;
  return returnTo === '/' ? `/api/auth/login?${REAUTH_QUERY}` : `/api/auth/login?${REAUTH_QUERY}&returnTo=${encodeURIComponent(returnTo)}`;
};

/** Shown when auth-gate.tsx sees `?authError=` on return from the IdP — the one bit of inline OIDC UI this design needs, everything else is a redirect. */
const OidcError = ({ message }: OidcErrorProps) => (
  <div className="grid min-h-dvh place-items-center bg-background p-6">
    <Card className="w-full max-w-lg">
      <CardHeader>
        <div className="mb-2 flex items-center gap-2 text-red-500">
          <CircleAlert aria-hidden className="size-5" />
          <span className="font-semibold">Sign-in failed</span>
        </div>
        <CardTitle>Couldn't complete sign-in</CardTitle>
        <CardDescription>{describeAuthError(message ?? null)} Try again — you'll be asked to sign in to Okta again. Contact an admin if this keeps happening.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <a href={buildRetryHref()}>Try again</a>
          </Button>
          {/* POST, not a link: logout GETs are ignored when speculative, and this must always clear cookies. */}
          <form action={`/api/auth/logout?${REAUTH_QUERY}`} method="post">
            <Button type="submit" variant="outline">
              Sign out and start over
            </Button>
          </form>
        </div>
        <p className="text-muted-foreground text-xs">Error code: {normalizeAuthErrorReason(message ?? null)}</p>
      </CardContent>
    </Card>
  </div>
);
OidcError.displayName = 'OidcError';

export default OidcError;
